import type { SessionData, SessionStorageAdapter } from './session';
import { isAuthStorageKey } from './session';

/**
 * localStorage-backed SessionStorageAdapter (contract in ./session).
 * Ported from the legacy admin (Core/client/admin/src/backend/auth/local-storage-adapter.js):
 * maps SessionData to/from the EXACT `usrs_*` / `perm_*` / `usrs_spmods` keys so a session is
 * shared with the legacy admin when served from the same origin. Preserves I18N_LANGUAGE on clear.
 */

const SESSION_KEYS = [
	'usrs_uid',
	'usrs_guid',
	'usrs_eml',
	'usrs_fn',
	'usrs_ln',
	'usrs_lgrurl',
	'usrs_at',
	'usrs_rt',
	'usrs_ad',
	'usrs_rd',
	'usrs_ld',
	'usrs_spmods',
	'active_site_id',
	'active_site_code'
];

function getItem(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function setOrRemove(key: string, value: string | null): void {
	try {
		if (value == null) localStorage.removeItem(key);
		else localStorage.setItem(key, value);
	} catch (e) {
		console.error(e);
	}
}

function toIsoLoginDate(value: string | null): string | null {
	if (value == null) return null;
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? value : d.toISOString();
}

export const sessionStorageAdapter: SessionStorageAdapter = {
	load() {
		const id = getItem('usrs_uid');
		const accessToken = getItem('usrs_at');
		const loginDate = getItem('usrs_ld');
		if (id == null && accessToken == null && loginDate == null) return null;

		const perms: Record<string, string> = {};
		try {
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i);
				if (k && k.startsWith('perm_')) perms[k] = localStorage.getItem(k) ?? '';
			}
		} catch {
			/* ignore */
		}

		const spmods = getItem('usrs_spmods');
		return {
			id,
			uid: getItem('usrs_guid'),
			email: getItem('usrs_eml'),
			firstName: getItem('usrs_fn'),
			lastName: getItem('usrs_ln'),
			accessToken,
			refreshToken: getItem('usrs_rt'),
			accessTokenDurationMinutes: getItem('usrs_ad'),
			refreshTokenDurationMinutes: getItem('usrs_rd'),
			loginDate,
			perms,
			specialModules: spmods && spmods.length > 0 ? spmods.split(';') : [],
			loginRedirectUrl: getItem('usrs_lgrurl')
		};
	},

	save(data: SessionData) {
		setOrRemove('usrs_uid', data.id);
		setOrRemove('usrs_guid', data.uid);
		setOrRemove('usrs_eml', data.email);
		setOrRemove('usrs_fn', data.firstName);
		setOrRemove('usrs_ln', data.lastName);
		setOrRemove('usrs_at', data.accessToken);
		setOrRemove('usrs_rt', data.refreshToken);
		setOrRemove('usrs_ad', data.accessTokenDurationMinutes);
		setOrRemove('usrs_rd', data.refreshTokenDurationMinutes);
		setOrRemove('usrs_ld', toIsoLoginDate(data.loginDate));
		if (data.loginRedirectUrl != null) setOrRemove('usrs_lgrurl', data.loginRedirectUrl);
		for (const [key, value] of Object.entries(data.perms ?? {})) setOrRemove(key, value == null ? null : String(value));
		const mods = Array.isArray(data.specialModules) ? data.specialModules : [];
		setOrRemove('usrs_spmods', mods.length > 0 ? mods.join(';') : null);
	},

	clear() {
		try {
			for (const key of SESSION_KEYS) localStorage.removeItem(key);
			const permKeys: string[] = [];
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i);
				if (k && k.startsWith('perm_')) permKeys.push(k);
			}
			for (const k of permKeys) localStorage.removeItem(k);
		} catch (e) {
			console.error(e);
		}
	},

	subscribe(cb: () => void) {
		const handler = (e: StorageEvent) => {
			if (isAuthStorageKey(e.key)) cb();
		};
		window.addEventListener('storage', handler);
		return () => window.removeEventListener('storage', handler);
	}
};

/** Non-React token getters for the axios interceptor. */
export function getAccessToken(): string | null {
	return getItem('usrs_at');
}
export function getRefreshToken(): string | null {
	return getItem('usrs_rt');
}
